import React from "react";
import { ListItem, Text, UnorderedList, VStack } from "@chakra-ui/react";

/**
 * AI-powered image & DAM features
 * (copy verified against ImageKit docs – April 2025)
 */
export const featureSection = [
  /* ──────────────────────────  GENERATIVE FILL  ───────────────────────── */
  {
    coloredSlab: true,
    title: "Generative Fill: extend any image",
    subHeading: (
      <>
        <Text>
          Resize an image to a new width or height and let <code>bg-genfill</code> intelligently
          paint the missing pixels. Ideal for turning portrait shots into hero banners, no reshoots,
          no awkward crops.
        </Text>
      </>
    ),
    graphic: {
      videoUrl:
        "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Gen%20Fill.mp4?updatedAt=17250225082506&tr=w-1250,so-1,eo-5",
      altText: "Generative Fill in action"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#generative-fill-bg-genfill",
    docLabel: "Docs"
  },

  /* ───────────────────────────  SMART CROP  ─────────────────────────── */
  {
    title: "Smart & object-aware cropping",
    subHeading: (
      <>
        <Text>
          One master image, many perfectly framed derivatives. Use{" "}
          <code>fo-auto</code> for smart crops, <code>fo-face</code> for face-centric thumbnails, or{" "}
          <code>fo-{"{object}"}</code> to focus on any of <strong>80 + supported objects</strong>-
          cars, dogs, shoes, and more—directly in the URL.
        </Text>
      </>
    ),
    graphic: {
      videoUrl:
        "https://ik.imagekit.io/ikmedia/Resize%20with%20AI-powered%20cropping.mp4?updatedAt=1720692818395&tr=w-1250,so-1,eo-8",
      altText: "AI cropping demo"
    },
    docLink: "https://imagekit.io/docs/image-resize-and-crop#auto-smart-cropping---fo-auto",
    docLabel: "Docs"
  },

  /* ─────────────────────────  CHANGE BACKGROUND  ─────────────────────── */
  {
    coloredSlab: true,
    title: "Change backgrounds",
    subHeading: (
      <>
        <Text>
          Replace dull or cluttered backdrops with on-brand scenes using{" "}
          <code>e-changebg-prompt-{"{text}"}</code>. Describe the environment—“warm wooden table
          studio lighting” or “snow-covered mountains at dusk”—and ImageKit composites it behind the
          subject with photo-realistic accuracy.
        </Text>
        <Text>
          Chain with other transformations
          like <code>e-dropshadow</code> to deliver ready-to-publish product shots at scale.
        </Text>
      </>
    ),
    graphic: {
      videoUrl:
        "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Homepage/Background%20change%202.mp4?updatedAt=1733121129435&tr=w-1250",
      altText: "AI background replacement"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#change-background-e-changebg",
    docLabel: "Docs"
  },

  /* ─────────────────────────  BG REMOVE + SHADOW  ────────────────────── */
  {
    title: "Remove backgrounds & add AI shadows",
    subHeading: (
      <>
        <Text>
          Isolate subjects with <code>e-removedotbg</code> (or cost-efficient{" "}
          <code>e-bgremove</code>) and append <code>e-dropshadow</code> for realistic shadow—no
          manual masking required.
        </Text>
        <Text>
          Need branding? Simply append an <code>l-image</code> overlay to stamp your logo in the same
          URL—consistent, repeatable, and fully automated.
        </Text>
      </>
    ),
    graphic: {
      url: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Product%20Images.jpg?updatedAt=1726032988785113&tr=w-623,dpr-2",
      altText: "Product images with background removed and shadows"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#ai-drop-shadow-e-dropshadow",
    docLabel: "Docs"
  },

  /* ───────────────────────  TEXT-TO-IMAGE  ─────────────────────────── */
  {
    coloredSlab: true,
    title: "Generate images from text prompts",
    subHeading: (
      <>
        <Text>
          Describe your visual in <code>ik-genimg-prompt-{"{text}"}</code> and ImageKit returns a
          brand-new, copyright-safe image—no stock fees, no design queue.
        </Text>
      </>
    ),
    graphic: {
      url: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Text%20prompt%20in%20URL.jpg?updatedAt=1726226940145&tr=w-1250",
      altText: "Image generated from prompt"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#generate-image-via-text-prompt",
    docLabel: "Docs"
  },

  /* ────────────────────────  VARIATIONS  ───────────────────────────── */
  {
    title: "Generate endless variations",
    subHeading: (
      <>
        <Text>
          Keep campaigns fresh with <code>e-genvar</code>. Start from one asset and instantly render
          limitless colour, fabric, or style variants—on demand.
        </Text>
      </>
    ),
    graphic: {
      videoUrl:
        "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Create%20new%20images.mp4?updatedAt=17250325000211&tr=w-1250",
      altText: "Generate image variations"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#generate-variations-of-an-image-e-genvar",
    docLabel: "Docs"
  },

  /* ────────────────────────────  UPSCALE  ───────────────────────────── */
  {
    coloredSlab: true,
    title: "Upscale low-resolution images",
    subHeading: (
      <>
        <Text>
          Boost resolution up to 4 × with the <code>e-upscale</code> transformation—produce crisp,
          detailed visuals from legacy or user-generated content in a single call.
        </Text>
      </>
    ),
    graphic: {
      url: "https://ik.imagekit.io/ikmedia/Graphics/AI%20Landing%20page/Upscale.jpg?updatedAt=17259446040340&tr=w-623,dpr-2",
      altText: "AI upscaling example"
    },
    docLink: "https://imagekit.io/docs/ai-transformations#upscale-e-upscale",
    docLabel: "Docs"
  },

  /* ─────────────────────  AI IN THE DAM  ───────────────────────────── */
  {
    title: "AI-powered DAM boosts productivity",
    subHeading:
      "ImageKit DAM weaves AI into everyday asset management, so teams spend less time organising and more time creating.",
    items: [
      {
        title: {
          strong: "Auto-tag every upload",
          rest:
            "Automatic image tagging on ingest enhances organisation and turbo-charges search."
        },
        graphic: {
          videoUrl:
            "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Centralised%20DAM/AI%20for%20faster%20organization.mp4?updatedAt=17140475183310&tr=w-1250",
          altText: "AI tagging in DAM",
          docLink: "https://imagekit.io/docs/dam/tagging-media-assets"
        }
      },
      {
        title: {
          strong: "AI-powered search",
          rest:
            "Find files by typing natural language or dropping an image to get visually similar results—instantly."
        },
        graphic: {
          videoUrl:
            "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/Centralised%20DAM/DAM%20AI%20search.mp4?updatedAt=1719480099871&tr=w-1250",
          altText: "AI smart search",
          docLink: "https://imagekit.io/docs/dam/search"
        }
      }
    ]
  }
];
